'use client';

import { cn } from '@insta-club/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import { usePathname, useRouter } from 'next/navigation';
import { PropsWithChildren, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props extends PropsWithChildren {
    open: boolean;
    openKey?: string;
    onClose: () => void;
    className?: string;
}

const Dialog: React.FC<Props> = ({ open, openKey = '#dialog', onClose, children, className }) => {
    const router = useRouter();
    const pathname = usePathname();
    const [mounted, setMounted] = useState(false);
    const [lastPushTimestamp, setLastPushTimestamp] = useState(0);

    useEffect(() => {
        setMounted(true);
        const handleHashChange = () => {
            if (window.location.hash !== openKey && open) {
                onClose();
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [open, onClose, openKey]);

    useEffect(() => {
        if (!mounted) return;

        const now = Date.now();
        if (now - lastPushTimestamp < 100) {
            return;
        }

        if (open) {
            if (window.location.hash !== openKey) {
                setLastPushTimestamp(now);
                router.push(`${pathname}${openKey}`, { scroll: false });
            }
        } else if (window.location.hash === openKey) {
            setLastPushTimestamp(now);
            router.back();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, openKey, mounted]);

    if (!mounted) return null;

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    className={cn('fixed inset-0 z-[9999] container flex flex-col bg-white', className)}
                >
                    {children}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default Dialog;

